import React, { useMemo } from 'react';

import TableLoader from 'components/utility/ContentLoader/TableLoader/TableLoader';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';
import Table from 'components/data-visualization/Table/Table';

import {
  TableHeader,
  HorizontalScrollContainer,
} from 'components/Overview/BreakdownTable/styles';
import Layout from 'components/styled/Layout/Layout';
import BreakdownTablePlaceholder from 'components/data-visualization/Table/BreakdownTablePlaceholder';

import useMedia from 'hooks/useMedia';
import BreakdownTableRow, {
  IBreakdownTableRowProps,
} from './BreakdownTableRow';
import { IMetric } from 'maps/metrics';
import { IBreakdownsItem } from './../types';
import { HeaderWrapper } from 'components/data-visualization/Table/DropdownHead';

interface IBreakdownTable {
  withIcon?: boolean;
  isPending: boolean;
  headers: string[];
  breakdowns?: IBreakdownsItem[];
  activeMetric: IMetric;
  RowComponent?: React.FC<IBreakdownTableRowProps>;
}

const BreakdownTable: React.FC<IBreakdownTable> = (props) => {
  const {
    headers,
    breakdowns = [],
    isPending,
    activeMetric,
    withIcon,
    RowComponent = BreakdownTableRow,
  } = props;

  const isLargeScreen = useMedia('(min-width: 50em)');

  /**
   * True if there are no breakdowns to display.
   * @returns {boolean}
   */
  const hasNoData = useMemo(() => {
    if (Array.isArray(breakdowns)) {
      return breakdowns.length === 0;
    }
    return !isPending;
  }, [breakdowns, isPending]);

  return (
    <HorizontalScrollContainer>
      <AnimatedLoadingWrapper loading={isPending}>
        <Layout.Relative>
          <Table>
            <thead>
              <tr>
                {headers.map((header) => (
                  <TableHeader key={header} isRightAligned>
                    <HeaderWrapper
                      condition={header
                        .toLowerCase()
                        .includes('this week to date')}
                    >
                      {header}
                    </HeaderWrapper>
                  </TableHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              {isPending && <TableLoader columnCount={headers.length} />}
              {hasNoData && !isPending && (
                <BreakdownTablePlaceholder
                  isSmallScreen={!isLargeScreen}
                  columnCount={headers.length}
                />
              )}
              {breakdowns.length > 0 &&
                !isPending &&
                breakdowns
                  .slice(0, 5)
                  .map(
                    (row: IBreakdownsItem): JSX.Element => (
                      <RowComponent
                        key={row?.label}
                        row={row}
                        withIcon={withIcon}
                        activeMetric={activeMetric}
                      />
                    )
                  )}
            </tbody>
          </Table>
        </Layout.Relative>
      </AnimatedLoadingWrapper>
    </HorizontalScrollContainer>
  );
};

export default BreakdownTable;
